import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowForwardRounded,
  CheckCircleOutlineRounded,
  DevicesRounded,
  LaptopMacRounded,
  PointOfSaleRounded,
  QrCodeScannerRounded,
  ShieldOutlined,
  ShoppingBagOutlined,
  SmartphoneRounded,
} from "@mui/icons-material";
import "./posDeviceGate.css";
import { isPosScreenEligible } from "./posDeviceRules";

// A responsive product-experience restriction, NOT an authentication/security control.
// Keep server-side authorization and register checks in place.
function meetsPosViewport() {
  if (typeof window === "undefined") return false;
  return isPosScreenEligible({
    viewportWidth: window.innerWidth,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    touchPrimary: window.matchMedia?.("(pointer: coarse)")?.matches || false,
  });
}

function usePosViewport() {
  const [allowed, setAllowed] = useState(meetsPosViewport);

  useEffect(() => {
    const sync = () => setAllowed(meetsPosViewport());
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    const pointer = window.matchMedia?.("(pointer: coarse)");
    if (pointer?.addEventListener) pointer.addEventListener("change", sync);
    else pointer?.addListener?.(sync);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      if (pointer?.removeEventListener) pointer.removeEventListener("change", sync);
      else pointer?.removeListener?.(sync);
    };
  }, []);

  return allowed;
}

function PosMobilePromotion() {
  return (
    <main className="pos-device-page">
      <div className="pos-device-orb pos-device-orb--one" aria-hidden="true" />
      <div className="pos-device-orb pos-device-orb--two" aria-hidden="true" />
      <header className="pos-device-topline">
        <Link to="/" className="pos-device-brand" aria-label="Universe Inventory home">
          <span className="pos-device-brand-icon"><PointOfSaleRounded /></span>
          <span>Universe<span className="pos-device-brand-accent">POS</span></span>
        </Link>
        <span className="pos-device-label"><ShieldOutlined /> Built for business</span>
      </header>

      <section className="pos-device-shell" aria-labelledby="pos-device-title">
        <div className="pos-device-intro">
          <span className="pos-device-eyebrow"><span className="pos-device-pulse" /> YOUR CHECKOUT, YOUR WAY</span>
          <h1 id="pos-device-title">Big business power. <span>Right in your pocket.</span></h1>
          <p className="pos-device-description">
            This web-based Inventory / Point of Sale terminal is designed for desktop
            screens and larger tablets. On a phone or smaller tablet, discover
            UniversePOS, our dedicated mobile experience.
          </p>
          <div className="pos-device-actions">
            <Link className="pos-device-primary" to="/store/universe-mobile/application">
              Explore our mobile apps <ArrowForwardRounded />
            </Link>
            <Link className="pos-device-secondary" to="/">
              Return to homepage
            </Link>
          </div>
          <p className="pos-device-availability">App availability and installation options are shown on the mobile-app page.</p>
          <div className="pos-device-capabilities" aria-label="UniversePOS features">
            <span><CheckCircleOutlineRounded /> Sales & checkout</span>
            <span><CheckCircleOutlineRounded /> Inventory operations</span>
            <span><CheckCircleOutlineRounded /> Barcode scanning</span>
          </div>
        </div>

        <div className="pos-device-showcase" aria-hidden="true">
          <div className="pos-device-glow" />
          <div className="pos-device-floating pos-device-floating--top">
            <span className="pos-device-floating-icon"><ShoppingBagOutlined /></span>
            <span><strong>Smart checkout</strong><small>Simplify daily sales</small></span>
          </div>
          <div className="pos-device-phone">
            <div className="pos-device-speaker" />
            <div className="pos-device-phone-top"><span className="pos-device-mini-logo">U<span>POS</span></span><span className="pos-device-phone-dot" /></div>
            <div className="pos-device-phone-welcome">Mobile workspace <strong>Stay in control.</strong></div>
            <div className="pos-device-total"><span>Checkout preview</span><strong>Fast. Simple. Smart.</strong><div className="pos-device-total-lines"><i /><i /><i /></div></div>
            <div className="pos-device-tile-row"><span><ShoppingBagOutlined /> Sales</span><span><QrCodeScannerRounded /> Scan</span></div>
            <div className="pos-device-product"><span className="pos-device-product-icon"><PointOfSaleRounded /></span><span><strong>UniversePOS</strong><small>Mobile inventory & checkout</small></span><ArrowForwardRounded /></div>
            <div className="pos-device-phone-button">Discover the mobile app <ArrowForwardRounded /></div>
          </div>
          <div className="pos-device-floating pos-device-floating--bottom">
            <span className="pos-device-floating-icon pos-device-floating-icon--soft"><SmartphoneRounded /></span>
            <span><strong>Made for mobile</strong><small>Explore the app store</small></span>
          </div>
        </div>
      </section>
      <footer className="pos-device-footer"><span><LaptopMacRounded /> Desktop POS available on computers</span><span><DevicesRounded /> Larger tablets and desktop displays supported</span></footer>
    </main>
  );
}

export default function PosDeviceGate({ children }) {
  const allowed = usePosViewport();
  return allowed ? children : <PosMobilePromotion />;
}
